import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    CircularProgress,
    Alert,
} from '@mui/material';
import { requestOperationOTP } from '../services/otpService';

const AdminOTPDialog = ({ open, onClose, operationType, onVerify }) => {
    const [otp, setOtp] = useState(['', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [otpRequested, setOtpRequested] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [timer, setTimer] = useState(300); // 5 minutes
    const inputRefs = [useRef(), useRef(), useRef(), useRef()];
    const timerIntervalRef = useRef(null);

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            setOtp(['', '', '', '']);
            setError('');
            setOtpRequested(false);
            setTimer(300);
        } else {
            // Clean up timer when dialog closes
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
        }
    }, [open]);

    // Timer countdown
    useEffect(() => {
        if (otpRequested && timer > 0) {
            timerIntervalRef.current = setInterval(() => {
                setTimer((prev) => {
                    if (prev <= 1) {
                        clearInterval(timerIntervalRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
        };
    }, [otpRequested, timer]);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const handleRequestOTP = async () => {
        setRequesting(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            await requestOperationOTP(operationType, token);
            setOtpRequested(true);
            setTimer(300);
            setTimeout(() => {
                inputRefs[0].current?.focus();
            }, 100);
        } catch (err) {
            setError(err.message || 'Failed to send OTP');
        } finally {
            setRequesting(false);
        }
    };

    const handleOtpChange = (index, value) => {
        if (value.length > 1) {
            value = value.slice(-1);
        }

        if (!/^[0-9]*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 3) {
            inputRefs[index + 1].current?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    const handleVerify = () => {
        const otpValue = otp.join('');
        if (otpValue.length !== 4) {
            setError('Please enter the complete 4-digit OTP');
            return;
        }

        setLoading(true);
        setError('');

        // Call parent's verify function with OTP
        onVerify(otpValue);
    };

    const handleResendOTP = () => {
        setOtp(['', '', '', '']);
        setError('');
        handleRequestOTP();
    };

    const getOperationLabel = (type) => {
        const labels = {
            fund_user: 'Fund User Wallet',
            bulk_operation: 'Bulk Fund Operation',
            suspend_user: 'Suspend User',
            approve_kyc: 'Approve KYC',
            reject_kyc: 'Reject KYC',
            settings_change: 'Settings Change',
        };
        return labels[type] || 'Admin Operation';
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h6" fontWeight="bold">
                    OTP Verification Required
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {getOperationLabel(operationType)}
                </Typography>
            </DialogTitle>

            <DialogContent>
                {!otpRequested ? (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body1" gutterBottom>
                            Click the button below to receive a One-Time Password (OTP) via email and push notification.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRequestOTP}
                            disabled={requesting}
                            sx={{ mt: 2 }}
                            fullWidth
                        >
                            {requesting ? <CircularProgress size={24} /> : 'Request OTP'}
                        </Button>
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
                            Enter the 4-digit code sent to your email and device
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
                            {otp.map((digit, index) => (
                                <TextField
                                    key={index}
                                    inputRef={inputRefs[index]}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    variant="outlined"
                                    inputProps={{
                                        maxLength: 1,
                                        style: { textAlign: 'center', fontSize: '24px', fontWeight: 'bold' },
                                    }}
                                    sx={{ width: 60 }}
                                />
                            ))}
                        </Box>

                        {timer > 0 ? (
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                OTP expires in: {formatTime(timer)}
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="error" textAlign="center">
                                OTP has expired
                            </Typography>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Button
                                variant="text"
                                color="primary"
                                onClick={handleResendOTP}
                                disabled={requesting || timer > 240} // Allow resend after 1 minute
                                size="small"
                            >
                                {requesting ? 'Sending...' : 'Resend OTP'}
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                {otpRequested && (
                    <Button
                        onClick={handleVerify}
                        variant="contained"
                        disabled={loading || otp.join('').length !== 4 || timer === 0}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Verify & Proceed'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AdminOTPDialog;
